import { Col, DatePicker, Form, Input, Modal, Row, Switch } from "antd";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import NormalButton from "../../../Reuseable/NormalButton/NormalButton";
import CustomSelect from "../../../Reuseable/Select/Select";
import {
  createTelecallerFeedback,
  getTelecallerActionCodes,
  getTelecallerFieldAgents,
} from "../../../api/Cases";
import { ReactComponent as Calender } from "../../../assets/Icons/calendarIcon.svg";
import "./style.scss";
import { StarFilled, StarOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import InputField from "../../../Reuseable/InputField/InputField";

export const CasesFeedbackModal = ({ open, setOpen, data, setRefresh }) => {
  const { token } = useSelector((state) => state.auth);
  const [form] = Form.useForm();
  const [assignToAgent, setAssignToAgent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldAgents, setFieldAgents] = useState([]);
  const [callStatus, setCallStatus] = useState([]);
  const [isPTP, setIsPTP] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [isCallback, setIsCallback] = useState(false);

  const onFinish = async (values) => {
    const subStatus = callStatus?.find(
      (item) => item?.id === values?.call_status_id,
    );
    const payload = {
      ...values,
      schedule_date: values.schedule_date
        ? values.schedule_date.format("YYYY-MM-DDTHH:mm:ssZ")
        : undefined,
      ptp_date_time: values?.ptp_date_time
        ? values?.ptp_date_time.format("YYYY-MM-DDTHH:mm:ssZ")
        : undefined,
      call_sub_status: subStatus?.value,
      xbucket_id: data?.id,
      callback_date_time: values?.callback_date_time
        ? values?.callback_date_time.format("YYYY-MM-DDTHH:mm:ssZ")
        : undefined,
    };

    if (typeof payload.call_attend_by === "string" && payload.call_attend_by.trim() === "") {
      delete payload.call_attend_by;
    }

    if (!assignToAgent) {
      delete payload.field_agent_id;
      delete payload.schedule_date;
    }
    setLoading(true);
    try {
      const res = await createTelecallerFeedback(payload, token);

      if (res?.status === 200) {
        toast.success(res?.data?.message || "Feedback submitted successfully");
        setLoading(false);
        form.resetFields();
        setAssignToAgent(false);
        setOpen(false);
        if (setRefresh) {
          setRefresh((prev) => !prev);
        }
      } else {
        setLoading(false);
        toast.error("Something went wrong");
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const phoneNumbers = Array.isArray(data?.cases_phone_numbers)
    ? [
        ...new Set(
          data.cases_phone_numbers
            .map((item) => item?.phonenumber)
            .filter(Boolean),
        ),
      ]
    : [];

  useEffect(() => {
    const fetchTelecallerFieldAgents = async () => {
      if (!open) return;
      try {
        const res = await getTelecallerFieldAgents(token);
        if (Array.isArray(res)) {
          setFieldAgents(res);
        } else {
          setFieldAgents([]);
        }
      } catch (error) {}
    };
    fetchTelecallerFieldAgents();
  }, [token, open]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setIsPTP(false);
      setIsPaid(false);
      setIsCallback(false);
    }
  }, [open]);

  useEffect(() => {
    const fetchTelecallerActionCodes = async () => {
      if (!open) return;
      try {
        const res = await getTelecallerActionCodes(token);

        if (Array.isArray(res)) {
          setCallStatus(res);
        } else {
          setCallStatus([]);
        }
      } catch (error) {}
    };
    fetchTelecallerActionCodes();
  }, [token, open]);

  const phoneNumberOptions = Array.isArray(data?.cases_phone_numbers)
    ? data.cases_phone_numbers
        .filter((item) => item?.phonenumber)
        .map((item) => ({
          value: item.phonenumber,
          label: (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>{item.phonenumber}</span>
              {item.is_starred ? (
                <StarFilled style={{ color: "#f5c518" }} />
              ) : (
                <StarOutlined style={{ color: "#ccc" }} />
              )}
            </div>
          ),
        }))
    : [];

  if (data?.cust_mobile_no && !phoneNumbers.includes(data.cust_mobile_no)) {
    phoneNumbers.push(data.cust_mobile_no);
  }

  return (
    <Modal
      open={open}
      title={
        <div className="cases-feedback-modal-header">
          <h3>Cases Feedback</h3>
          <div className="user-info">
            <h3>{data?.customer_name || "--"}</h3>
            <p>Case Id : {data?.id}</p>
          </div>
        </div>
      }
      footer={null}
      width={700}
      onCancel={() => setOpen(false)}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Phone Number"
              name="phone_number"
              rules={[
                { required: true },
                {
                  pattern: /^[0-9]{10}$/,
                  message: "Enter valid 10 digit number",
                },
              ]}
            >
              <CustomSelect
                placeholder="Select phone number"
                options={phoneNumberOptions}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Call Status"
              name="call_status_id"
              rules={[{ required: true }]}
            >
              <CustomSelect
                placeholder="Select Call Status"
                options={callStatus?.map((item) => ({
                  value: item?.id,
                  label: item?.key,
                }))}
                onChange={(val) => {
                  const selectedKey = callStatus?.find(
                    (item) => item?.id === val,
                  )?.key;
                  setIsPTP(selectedKey === "PTP");
                  setIsPaid(selectedKey === "PAID");
                  setIsCallback(selectedKey === "CALLBACK");
                }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Call Attended By" name="call_attend_by">
              <InputField placeholder="Customer" />
            </Form.Item>
          </Col>
          {isPTP && (
            <Col span={12}>
              <Form.Item
                label="Payment Date & Time"
                name="ptp_date_time"
                rules={[{ required: isPTP }]}
              >
                <DatePicker
                  style={{ width: "100%", height: "43px" }}
                  placeholder="Select date & time"
                  className="form-date-picker"
                  suffixIcon={<Calender />}
                  showTime={{
                    format: "HH:mm",
                  }}
                  format="YYYY-MM-DD HH:mm"
                />
              </Form.Item>

              <Form.Item
                label="PTP Amount"
                name="ptp_amount"
                rules={[{ required: isPTP }]}
              >
                <InputField type="number" />
              </Form.Item>
            </Col>
          )}

           {
            isCallback && (
              <Col span={12}>
                <Form.Item
                  label="Callback Date & Time"
                  name="callback_date_time"
                  rules={[{ required: true }]}
                >
                  <DatePicker
                    style={{ width: "100%", height: "43px" }}
                    placeholder="Select date & time"
                    className="form-date-picker"
                    suffixIcon={<Calender />}
                    showTime={{
                      format: "HH:mm",
                    }}
                    format="YYYY-MM-DD HH:mm"
                  />
                </Form.Item>
              </Col>
            )
           }


          <Col span={24}>
            <Form.Item label="Comment" name="comment">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label="Notes" name="notes">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Paid Amount"
              name="paid_amount"
              rules={[
                { required: isPaid, message: "Please enter paid amount" },
              ]}
            >
              <InputField
                placeholder="Enter paid amount"
                min={0}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Assign to Field Agent"
              name="assign_to_field_agent"
              valuePropName="checked"
            >
              <Switch onChange={setAssignToAgent} />
            </Form.Item>
          </Col>

          {assignToAgent && (
            <>
              <Col span={12}>
                <Form.Item
                  label="Field Agent ID"
                  name="field_agent_id"
                  rules={[{ required: true }]}
                >
                  <CustomSelect
                    //   label="Mother Tongue"
                    placeholder="Select Field agent"
                    options={fieldAgents?.map((item) => ({
                      value: item?.id,
                      label: item?.name,
                    }))}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Schedule Date & Time"
                  name="schedule_date"
                  rules={[{ required: true }]}
                >
                  <DatePicker
                    style={{ width: "100%", height: "43px" }}
                    placeholder="Select date & time"
                    className="form-date-picker"
                    suffixIcon={<Calender />}
                    showTime={{
                      format: "HH:mm",
                    }}
                    format="YYYY-MM-DD HH:mm"
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>

        <Row justify="end">
          <Col>
            <NormalButton htmlType="submit" loading={loading} type="submit">
              Submit Feedback
            </NormalButton>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
